import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import ReportWidget from "../components/ReportWidgets";
import DeleteReportDialog from "../components/DeleteReportDialog";
import ReportShareDialog from "../components/ReportShareDialog";
import {
  CircleNotch, ArrowsClockwise, Sparkle, FilePdf, Trash, CaretLeft, X,
  PaperPlaneRight, UsersThree, Clock, PencilSimple, LockSimple,
} from "@phosphor-icons/react";

function EditPanel({ report, onClose, onUpdated }) {
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (!instruction.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${report.id}/ai-edit`, { instruction: instruction.trim() });
      toast.success("Report updated");
      setInstruction("");
      onUpdated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const QUICK = ["Add a summary at the top", "Add a table of the detail", "Change the chart to a bar chart", "Add monthly trend line chart", "Limit to top 5"];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="report-edit-panel">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={busy ? undefined : onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#7C3AED]/10"><Sparkle size={18} weight="fill" className="text-[#7C3AED]" /></div>
            <div>
              <p className="font-heading text-base font-black tracking-tight text-[#0A0A0A]">AI report editor</p>
              <p className="text-[11px] text-neutral-500">Describe the change — charts, tables, summaries</p>
            </div>
          </div>
          <button data-testid="report-edit-close" onClick={onClose} className="rounded-sm p-1.5 text-neutral-500 hover:bg-neutral-100"><X size={18} weight="bold" /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <p className="text-sm text-neutral-500">Tell the assistant how to change "<span className="font-semibold text-neutral-700">{report.title}</span>". It can add, remove or restyle widgets and rewrite summaries.</p>
          <div className="space-y-2">
            {QUICK.map((q) => (
              <button key={q} data-testid="report-edit-quick" onClick={() => setInstruction(q)}
                className="block w-full rounded-sm border border-neutral-200 px-3 py-2 text-left text-sm text-neutral-600 transition-all duration-200 hover:border-[#7C3AED] hover:text-[#7C3AED]">{q}</button>
            ))}
          </div>
        </div>

        <div className="border-t border-neutral-200 p-3">
          <div className="flex items-end gap-2">
            <textarea data-testid="report-edit-input" value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); apply(); } }}
              placeholder="e.g. Add jobs by status as a pie chart"
              className="max-h-28 flex-1 resize-none rounded-sm border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-[#7C3AED]" />
            <button data-testid="report-edit-apply" onClick={apply} disabled={busy || !instruction.trim()}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-[#7C3AED] text-white hover:bg-[#6B21C8] disabled:opacity-50">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneRight size={16} weight="bold" />}
            </button>
          </div>
          <p className="mt-1.5 text-[10px] text-neutral-400">AI-generated — verify figures against the source pages.</p>
        </div>
      </div>
    </div>
  );
}

export default function ReportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [managingAccess, setManagingAccess] = useState(false);

  const load = useCallback(() => {
    api.get(`/reports/${id}`).then((r) => setReport(r.data)).catch(() => setNotFound(true));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/reports/${id}/refresh`);
      setReport(data);
      toast.success("Data refreshed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const toggleShare = async () => {
    setSharing(true);
    try {
      const { data } = await api.put(`/reports/${id}`, { client_visible: !report.client_visible });
      setReport(data);
      toast.success(data.client_visible ? "Now visible in the client portal" : "Hidden from clients");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSharing(false);
    }
  };

  const del = () => setDeleting(true);

  if (notFound) return <Layout><div className="flex h-60 flex-col items-center justify-center gap-3 text-neutral-400" data-testid="report-not-found"><p>Report not found or you don't have access.</p><button onClick={() => navigate("/reports")} className="text-sm font-semibold text-[#0055FF] hover:underline">← Back to reports</button></div></Layout>;
  if (!report) return <Layout><div className="flex h-60 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;

  return (
    <Layout wide>
      <div data-testid="report-view-page">
        {/* Toolbar — pushed below the fixed "Ask AI" pill on large screens */}
        <div className="no-print flex flex-wrap items-center justify-between gap-3 lg:mt-14">
          <button data-testid="report-back" onClick={() => navigate("/reports")} className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-500 hover:text-[#0055FF]"><CaretLeft size={15} weight="bold" /> Reports</button>
          <div className="flex flex-wrap items-center gap-2">
            {report.can_edit && (
              <button data-testid="report-share-toggle" onClick={toggleShare} disabled={sharing}
                className={`inline-flex items-center gap-1.5 rounded-sm border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${report.client_visible ? "border-[#059669] bg-[#059669]/10 text-[#059669]" : "border-neutral-300 bg-white text-neutral-700 hover:border-[#059669]"}`}>
                {sharing ? <CircleNotch size={15} className="animate-spin" /> : <UsersThree size={15} weight="bold" />} {report.client_visible ? "Visible to clients" : "Visible to clients"}
              </button>
            )}
            {report.can_edit && (
              <button data-testid="report-manage-access" onClick={() => setManagingAccess(true)}
                className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:border-[#0055FF]">
                <LockSimple size={15} weight="bold" /> {report.visibility === "team" ? "All admins" : `Private${(report.viewer_admin_ids || []).length ? ` · ${report.viewer_admin_ids.length}` : ""}`}
              </button>
            )}
            <button data-testid="report-refresh" onClick={refresh} disabled={refreshing}
              className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:border-[#0055FF] disabled:opacity-50">
              <ArrowsClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} /> Refresh data
            </button>
            <button data-testid="report-pdf" onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:border-[#0055FF]">
              <FilePdf size={15} weight="bold" /> Download PDF
            </button>
            {report.can_edit && (
              <>
                <button data-testid="report-edit" onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#7C3AED] px-3 py-2 text-sm font-semibold text-white hover:bg-[#6B21C8]"><PencilSimple size={15} weight="bold" /> Edit with AI</button>
                <button data-testid="report-delete" onClick={del}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-600 hover:border-[#DC2626] hover:text-[#DC2626]"><Trash size={15} weight="bold" /></button>
              </>
            )}
          </div>
        </div>

        {/* Printable area */}
        <div id="report-print-root" className="mt-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Report</p>
              <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]">{report.title}</h1>
              {report.description && <p className="mt-2 max-w-3xl text-base text-neutral-600">{report.description}</p>}
            </div>
            <div className="text-right text-xs text-neutral-400">
              <p className="inline-flex items-center gap-1"><Clock size={12} /> Data as of {new Date(report.refreshed_at).toLocaleString()}</p>
              <p className="mt-0.5">Created by {report.created_by_name || "—"}</p>
            </div>
          </div>

          {report.widgets.length === 0 ? (
            <div className="mt-8 flex h-40 items-center justify-center rounded-sm border border-dashed border-neutral-300 text-neutral-400">This report has no widgets yet. Use "Edit with AI" to add some.</div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {report.widgets.map((w) => <ReportWidget key={w.id} w={w} data={report.data} />)}
            </div>
          )}

          <p className="mt-6 text-xs text-neutral-400">AI-generated dashboard from Asset Hub imported data — verify key figures against the source pages.</p>
        </div>
      </div>

      {editing && <EditPanel report={report} onClose={() => setEditing(false)} onUpdated={(r) => setReport(r)} />}
      {deleting && <DeleteReportDialog report={report} onClose={() => setDeleting(false)} onDeleted={() => navigate("/reports")} />}
      {managingAccess && <ReportShareDialog report={report} onClose={() => setManagingAccess(false)} onUpdated={(r) => { setReport(r); setManagingAccess(false); }} />}
    </Layout>
  );
}
